// What the Calendar keeps in this browser between visits: which layers were on, what they were called,
// each site's name and logo. It names people (a shared calendar is often someone's address) and
// businesses, so it belongs to the account that saw it: every key carries the Wix user and Symphony
// metasite, nothing is read for another account, and sign-out drops it all.

import type { Me } from '../me';

const PREFIX = 'symphony.calendar.';

/** Whose calendar this is — the Wix user and their Symphony metasite — or undefined until the user is known. */
export function calendarOwnerKey(me: Pick<Me, 'userId' | 'metaSiteId'> | null | undefined): string | undefined {
  return me?.userId ? `${me.userId}:${me.metaSiteId || 'no-metasite'}` : undefined;
}

const keyFor = (name: string, ownerKey: string) => `${PREFIX}${ownerKey}.${name}`;

/** The stored value, or undefined when there is none, no user yet, or storage is blocked. */
export function readCalendarStore<T>(name: string, ownerKey: string | undefined): T | undefined {
  if (!ownerKey) return undefined;
  try {
    const raw = window.localStorage.getItem(keyFor(name, ownerKey));
    return raw ? (JSON.parse(raw) as T) : undefined;
  } catch {
    return undefined;
  }
}

export function writeCalendarStore(name: string, ownerKey: string | undefined, value: unknown): void {
  if (!ownerKey) return;
  try {
    window.localStorage.setItem(keyFor(name, ownerKey), JSON.stringify(value));
  } catch {
    // Private windows and blocked storage just start from nothing next time.
  }
}

/** Everything the Calendar stored, for every account — the sign-out boundary. */
export function clearCalendarStorage(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key?.startsWith(PREFIX)) keys.push(key);
    }
    for (const key of keys) window.localStorage.removeItem(key);
  } catch {
    // Nothing to clear when storage is blocked.
  }
}
